<template>
  <TransitionRoot as="template" :show="open" @after-leave="emits('after-leave')">
    <Dialog :initial-focus="initialFocus" as="div" class="fixed inset-0 overflow-y-hidden"
            :class="zMax ? 'z-max' : 'z-max-2'"
            @close="handleClose(true)">
      <div class="flex min-h-screen items-center justify-center px-4 text-center hide-scrollbar sm:block">
        <TransitionChild v-if="!hideDialogOverlay" as="template" enter="ease-out duration-300" enter-from="opacity-0"
                         enter-to="opacity-100"
                         leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-slate-800 bg-opacity-80 transition-opacity"/>
        </TransitionChild>
        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300"
                         enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                         enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                         leave-from="opacity-100 translate-y-0 sm:scale-100"
                         leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <DialogPanel :class="dialogPanelClasses">
            <Component :is="isForm ? 'form' : 'div'" @submit.prevent="handleForm">
              <div :class="dialogContentClasses">
                <slot/>
              </div>
              <slot name="bottom"/>
            </Component>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import {Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {computed} from "vue";

const emits = defineEmits(["update:open", "cancel", "submit", "after-leave"]);
const props = defineProps({
  open: Boolean,
  addPadding: Boolean,
  maxWMd: Boolean,
  maxWLg: Boolean,
  maxWXl: Boolean,
  maxWTwoXl: Boolean,
  maxWFourXl: Boolean,
  dialogClasses: [String, Boolean],
  dialogBgBlack: Boolean,
  dialogBgTransparent: Boolean,
  hideDialogOverlay: Boolean,
  fullscreenMobile: Boolean,
  scrollBody: Boolean,
  loading: Boolean,
  initialFocus: {
    type: Object, // HTMLElement, but we don't use typescript
    default: null,
  },
  isForm: Boolean,
  zMax: Boolean,
});

const handleClose = (wasClickOutside) => {
  emits("cancel", wasClickOutside);
  emits("update:open", false);
};

const handleForm = (e) => emits("submit", e);

const dialogPanelClasses = computed(() => {
  let classes;

  if (props.fullscreenMobile) {
    classes = ["fixed inset-0 z-10 overflow-y-auto lg:relative lg:inline-block lg:overflow-hidden lg:rounded-2xl max-h-modal-lg lg:shadow-xl lg:transition-all lg:my-8 lg:align-middle lg:hide-scrollbar"]; // Override classes for fullscreen
    if (props.addPadding) classes.push("p-4"); // Optional: add padding for fullscreen mobile view
  } else {
    classes = ["relative inline-block overflow-hidden rounded-2xl text-left align-bottom shadow-xl transition-all -mt-8 sm:my-8 sm:align-middle hide-scrollbar"];
    classes.push(props.scrollBody ? "max-h-[calc(100vh-4rem)] overflow-hidden" : "max-h-modal");
    if (props.maxWTwoXl) classes.push("w-full sm:max-w-2xl");
    else if (props.maxWFourXl) classes.push("w-full sm:max-w-4xl");
    else if (props.maxWXl) classes.push("w-full sm:max-w-xl");
    else if (props.maxWLg) classes.push("w-full sm:max-w-lg");
    else if (props.maxWMd) classes.push("w-full sm:max-w-md");
  }

  if (props.dialogClasses) classes.push(props.dialogClasses);

  return classes;
});

const dialogContentClasses = computed(() => {
  let classes = ["p-0"];

  if (props.scrollBody) classes.push("flex max-h-[calc(100vh-4rem)] flex-col");
  if (!props.fullscreenMobile && props.addPadding) classes.push("px-4 pt-5 pb-4 sm:p-6 sm:pb-4"); // Adjust padding if not in fullscreen mode
  if (props.dialogBgBlack) classes.push("bg-black flex items-center justify-center");
  else if (props.dialogBgTransparent) classes.push("bg-transparent flex items-center justify-center");
  else classes.push("bg-white");

  return classes;
});
</script>

<style scoped>
.max-h-modal {
  max-height: 80vh;
  overflow-y: auto;
}

@media (min-width: 640px) {
  .max-h-modal {
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
  }
}

@media (min-width: 1024px) {
  .max-h-modal-lg {
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
  }
}
</style>
